const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

// Reusable helper function for authenticated API calls
const callApi = async (endpoint, token, options = {}) => {
    if (!token) {
        throw new Error('Authentication token is required for this action.');
    }

    const config = {
        method: 'POST', // Default to POST
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
        ...options,
    };

    if (options.body && typeof options.body !== 'string') {
        config.body = JSON.stringify(options.body);
    }

    const response = await fetch(`${API_URL}${endpoint}`, config);

    // For DELETE requests, which might not have a JSON body
    if (response.status === 204 || response.headers.get('content-length') === '0') {
        if (!response.ok) {
            throw new Error(`API request to ${endpoint} failed with status ${response.status}`);
        }
        return; // No content to parse
    }

    const data = await response.json();
    if (!response.ok) {
        throw new Error(data.error || `API request to ${endpoint} failed with status ${response.status}`);
    }
    return data;
};

// Admin function to get all users
export const getUsers = async (token) => {
    return callApi('/admin/users', token, { method: 'GET' });
};

// New function to get all locations for the admin panel
export const getAllLocations = async (token) => {
    return callApi('/admin/locations', token, { method: 'GET' });
};

// Function to add a new location
export const addLocation = async (locationData, city, token) => {
    return callApi(`/locations/${city}`, token, {
        method: 'POST',
        body: { point: locationData },
    });
};

// Function to update an existing location
export const updateLocation = async (locationId, locationData, city, token) => {
    return callApi(`/locations/${city}/${locationId}`, token, {
        method: 'PUT',
        body: locationData,
    });
};

// Function to delete a location
export const deleteLocation = async (locationId, city, token) => {
    const response = await fetch(`${API_URL}/locations/${city}/${locationId}`, {
        method: 'DELETE',
        headers: {
            'Authorization': `Bearer ${token}`,
        },
    });

    if (!response.ok) {
        const data = await response.json().catch(() => ({})); // Try to get error message
        throw new Error(data.error || `Failed to delete location with status ${response.status}`);
    }
    
    // Check if there's content to return
    const contentType = response.headers.get("content-type");
    if (contentType && contentType.indexOf("application/json") !== -1) {
        return response.json();
    } else {
        return { message: 'Location deleted successfully' }; // Or just return nothing
    }
};

// Admin function to toggle admin status for a user
export const toggleUserAdminStatus = async (userId, token) => {
    return callApi(`/admin/users/${userId}/toggle-admin`, token, { method: 'POST' });
};

// Admin function to toggle verified status for a user
export const toggleUserVerifiedStatus = async (userId, token) => {
    return callApi(`/admin/users/${userId}/toggle-verified`, token, { method: 'POST' });
};

// Admin function to toggle disabled status (ban/unban) for a user
export const toggleUserDisabledStatus = async (userId, token) => {
    return callApi(`/admin/users/${userId}/toggle-disabled`, token, { method: 'POST' });
};

// Admin function to permanently delete a user
export const deleteUser = async (userId, token) => {
    return callApi(`/admin/users/${userId}`, token, { method: 'DELETE' });
}; 

// Admin API calls
export const getPendingSuggestions = async (token) => {
    const response = await fetch(`${API_URL}/admin/suggestions`, {
        headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!response.ok) throw new Error('Failed to fetch suggestions.');
    return response.json();
};

export const approveSuggestion = async (token, suggestionId) => {
    const response = await fetch(`${API_URL}/admin/suggestions/${suggestionId}/approve`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!response.ok) throw new Error('Failed to approve suggestion.');
    return response.json();
};

export const rejectSuggestion = async (token, suggestionId) => {
    const response = await fetch(`${API_URL}/admin/suggestions/${suggestionId}/reject`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!response.ok) throw new Error('Failed to reject suggestion.');
    return response.json();
}; 